import { useMemo, useState } from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
} from "@tenant/ui";

import defaultStyles from "./PackageChildAgePicker.module.scss";

export type PackageChildAgePickerProps = PlatformComponentProps & {
  onAgeSelectHandler: (age: number) => void;
  childAge: number;
  label?: string;
  /** First selectable age (inclusive). Default 0. */
  minAge?: number;
  /** Last selectable age (inclusive). Default 17. */
  maxAge?: number;
};

/**
 * ChildAgePicker variant for the package search context.
 * Identical to the hotel ChildAgePicker except that it accepts `minAge` and
 * `maxAge` props so infant ages (0-1) can be excluded when a flight leg is
 * included in the package.
 */
export const PackageChildAgePicker = ({
  onAgeSelectHandler,
  childAge,
  label,
  minAge = 0,
  maxAge = 17,
}: PackageChildAgePickerProps) => {
  const { t } = useI18nContext();
  const COMPONENT_KEY = CoreHotelsComponent.ChildAgePicker;
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleAgeSelect = (age: number) => {
    onAgeSelectHandler(age);
    setIsPopoverOpen(false);
  };

  const ageOptions = useMemo(() => {
    return Array.from({ length: maxAge - minAge + 1 }, (_, i) => {
      const age = minAge + i;
      return {
        key: `${age}`,
        value: `${age}`,
        label:
          age === 0
            ? t("core-hotels.search.guestPicker.child.underOneAge")
            : `${age}`,
      };
    });
  }, [minAge, maxAge, t]);

  const selectedAgeLabel =
    childAge != null && childAge >= minAge && childAge <= maxAge
      ? childAge === 0
        ? t("core-hotels.search.guestPicker.child.underOneAge")
        : `${childAge}`
      : t("core-hotels.search.guestPicker.child.selectPlaceHolder");

  const ageRadio = (
    <RadioGroup
      className={cn("radio")}
      options={ageOptions}
      selectedOption={childAge != null ? `${childAge}` : ""}
      onSelectValue={(value) => handleAgeSelect(Number(value))}
    />
  );

  const triggerContent = (
    <>
      {selectedAgeLabel}
      <Icon
        className={cn("dialog-select-icon")}
        iconName={IconNameEnum.dropdownTriangle}
      />
    </>
  );

  return (
    <div className={block}>
      {label && <label className={cn("label")}>{label}</label>}
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger className={cn("child-age-button", { popover: true })}>
          {triggerContent}
        </PopoverTrigger>
        <PopoverContent
          className={cn("popover-content")}
          showCloseButton={false}
        >
          {ageRadio}
        </PopoverContent>
      </Popover>

      <Dialog>
        <DialogTrigger className={cn("child-age-button", { dialog: true })}>
          {triggerContent}
        </DialogTrigger>
        <DialogContent
          title={t("core-hotels.search.guestPicker.child.ageDialog.title")}
          className={cn("dialog-content")}
          fullScreen={true}
          bottomFixedContent={
            <DialogClose className={cn("apply-button")}>
              {t("core-hotels.search.guestPicker.child.ageDialog.applyButton")}
            </DialogClose>
          }
        >
          {ageRadio}
        </DialogContent>
      </Dialog>
    </div>
  );
};
